import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import Modal from 'react-modal';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import classNames from 'classnames';
import { geocodeByAddress } from 'react-places-autocomplete';

import * as actions from '../../actions';
import * as _actionsJourney from '../../actions/journey1';
import * as _actionsJourney2 from '../../actions/journey2';
import * as _actionsJourney3 from '../../actions/journey3';
import * as _actionsBasket from '../../actions/basket';
import * as _actionsProduct from '../../actions/product';
import * as _actionsFilters from '../../actions/filters';
import { RootState } from '../../reducers';
import {
  getBasketBeforeLogin,
  isComingFromAgency,
  isComingFromJ1,
  isScriptLoadedInHead,
  removeBasketBeforeLogin,
  renderSwitch,
  saveBasketBeforeLogin,
} from '../../utils/react';
import { getParameterByName } from '../../utils/toSearchParams';
import { isAuthenticated } from '../../utils/keycloak';
import ProgressBar from '../../components/ProgressBarV2';
import './Journey2.scss';
import BreadCrumbs from '../../components/BreadCrumbs';
import RouterContext from '../RouterContext';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import ResultsStep from './ResultsStep';
import { default as Step1J3 } from '../Journey3/Step1';
import Summary from '../Summary';
import OrderConfirmation from '../OrderConfirmation';
import SpinnerV2 from '../../components/SpinnerV2';
import Page404 from '../Page404';
import { TypeContainer } from '../../actions/typeContainer';
import { useParams } from 'react-router-dom';
import { usePrevious, useUpdateEffect } from 'react-use';
import Basket from '../../components/Basket';
import { ServiceDesk } from '../../components/ServiceDesk';
import { ModalContainer } from '../../components/ModalContainer';
import { LoginRequired } from '../../components/ProtectedComponent';
import { SchemaProduct, SchemaIndustry, SchemaPossibleBookingDates } from '@repo/legacy-commerce-api';
import { PartiallyIntegratedUserDataEC } from '../../reducers/app';

Modal.setAppElement('#root');

interface Props {
  actions: any;
  actionsJourney: typeof _actionsJourney;
  actionsJourney2: typeof _actionsJourney2;
  actionsJourney3: typeof _actionsJourney3;
  actionsBasket: typeof _actionsBasket;
  actionsProduct: typeof _actionsProduct;
  actionsFilters: typeof _actionsFilters;
  big5Industries: Record<string, SchemaIndustry>;
  dataPrefilledJ3: boolean;
  filters: any;
  history: any;
  industries: any;
  intl: any;
  isDrawRadius: boolean;
  keycloakReducer: any;
  keycloakReady: boolean;
  loading: { general: boolean; mapProduct?: boolean };
  location: any;
  nextProduct: (SchemaProduct & { idx: number }) | null;
  possibleBookingDates: SchemaPossibleBookingDates;
  isAgencyJourney: boolean;
  prevProduct: (SchemaProduct & { idx: number }) | null;
  product: any;
  productsJ2: SchemaProduct[];
  startDate: Date;
  urlHistory: Array<string>;
  userDataEC: PartiallyIntegratedUserDataEC;
  errors: any;
  locations: any;
  productsJ2ByUrn: Record<string, SchemaProduct>;
  containers: TypeContainer;
  dispatch: any;
}

const Journey2 = (props: Props) => {
  const checkoutStep: number = 7;
  let gettingJ3Data: boolean = false;

  const params = useParams();
  const prevParams = usePrevious(params);
  const prevKeycloakReady = usePrevious(props.keycloakReady);
  const prevUserDataEC = usePrevious(props.userDataEC);

  const [readyToRender, setReadyToRender] = useState<boolean>(false);
  const [step6Loading, setStep6Loading] = useState<boolean>(false);
  const [mapView, setMapView] = useState<boolean>(false);

  const { location, history, keycloakReady, keycloakReducer, loading, urlHistory, intl, containers } = props;

  useEffect(() => {
    if (isComingFromJ1(urlHistory)) {
      props.actions.clearData('basket');
    }

    (window as any).gApiLoaded = new Event('gApiLoaded');
    (window as any).addEventListener('gApiLoaded', scriptLoaded);

    if (props.keycloakReady) {
      preFetchData();
    }

    if (isComingFromAgency(urlHistory) && isScriptLoadedInHead('gapi2') && (window as any)?.google) {
      setReadyToRender(true);
    }

    return () => {
      window.removeEventListener('gApiLoaded', scriptLoaded);
    };
  }, []);

  useUpdateEffect(() => {
    const {
      userDataEC: { industry },
    } = props;
    const prevStep = prevParams.step as unknown as number;
    const step = params.step as unknown as number;

    if (step == 4 && prevStep != 4) {
      setMapView(false);
    }

    if (props.keycloakReady && prevKeycloakReady !== props.keycloakReady) {
      preFetchData();
    }

    getJ3Data();

    if (step < prevStep && step <= checkoutStep) {
      cancelOrConfirmBasket();
    }

    if (prevUserDataEC.industry !== industry && industry && !props?.userDataEC?.isAgencyJourney) {
      informAboutChangingIndustry();
    }

    // For django page
    if (step !== prevStep && step > 1) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [params, props.userDataEC.industry]);

  useUpdateEffect(() => {
    const {
      productsJ2,
      location: { search },
    } = props;
    const currentUrn = getParameterByName('urn', search);

    if (currentUrn && productsJ2?.length > 0) {
      props.actionsProduct.fetchProducts({ urn: currentUrn });
    }
  }, [props.location.search]);

  // Check for pre-saved basket on steps 1, 2, 3
  useEffect(() => {
    const step = params.step as unknown as number;
    if ([1, 2, 3].includes(Number(step))) {
      const preSavedBasket = getBasketBeforeLogin();
      if (preSavedBasket && preSavedBasket?.basketId && preSavedBasket?.basketReference) {
        const payload = {
          basket: preSavedBasket.basketId,
          basketreference: preSavedBasket.basketReference,
        };
        props.actionsBasket
          .fetchBasketInfo(payload)
          .catch((error) => console.error('Error', error))
          .then(() => {
            props.actionsBasket
              .getBasketDefinition(payload)
              .then(() => {
                props.actionsBasket.getBasketContent(payload);
              })
              .catch((error) => console.error('Error', error));
            removeBasketBeforeLogin();
          });
      }
    }
  }, [params.step]);

  useUpdateEffect(() => {
    if (
      props.userDataEC.isAgencyJourney &&
      props.userDataEC?.industry &&
      props.userDataEC?.agencyData?.customer?.branche
    ) {
      fetchIndustries();
    }
  }, [props.userDataEC.industry]);

  const handleLoginRedirect = () => {
    const { basket, basketreference } = props?.userDataEC?.basketInfo;
    // Before redirecting to Keycloak for login, save the basket and basketreference in localstorage
    if (basket && basketreference) {
      saveBasketBeforeLogin(basket.toString(), basketreference);
    }
  };

  const fetchIndustries = () => {
    let extraParam = null;
    if (props.userDataEC.basketInfo) {
      extraParam = {
        basket: props.userDataEC.basketInfo?.basket,
        basketreference: props.userDataEC.basketInfo?.basketreference,
      };
    }
    props.actionsFilters.fetchIndustries({ context: 'alcohol', ...extraParam });
    props.actionsFilters.fetchIndustries({ context: 'big5', ...extraParam });
  };

  const scriptLoaded = () => {
    setReadyToRender(true);
  };

  const preFetchData = () => {
    props.actionsFilters.fetchBookingDates();
    fetchIndustries();
    if (isAuthenticated()) {
      props.actionsJourney.getUserInfo();
    }
  };

  const getJ3Data = () => {
    const {
      containers: { isJourney3 },
      actionsJourney3,
    } = props;

    const basketId: number = params.step as unknown as number;
    const step: number = params.step as unknown as number;

    if (keycloakReady && readyToRender && isJourney3 && !gettingJ3Data && !props.dataPrefilledJ3) {
      if (basketId && step > 100) {
        gettingJ3Data = true;
        actionsJourney3.getUserData({ basket: basketId }).then((res) => {
          gettingJ3Data = false;
          res &&
            res.data.coordinate.forEach((loc) => {
              geocodeByAddress(loc.name)
                .then((results) => {
                  actionsJourney3.updateLocation({
                    name: loc.name,
                    data: results[0],
                  });
                })
                .catch((error) => console.error('Error', error));
            });
        });
      }
    }
  };

  const cancelOrConfirmBasket = () => {
    //"the possible states are: 0 - selection, 1 - reserved, -1 - invalid, 2 - booked.
    // If you set autocancel=1, a reserved basket would be set back to state 0 and can be
    // worked on again"
    if (props.userDataEC.basketInfo) {
      props.actionsJourney
        .getBasketState({
          basket: props.userDataEC.basketInfo.basket,
          basketreference: props.userDataEC.basketInfo.basketreference || '',
          autocancel: props.userDataEC.basketInfo?.reservationDetails ? 1 : 0,
        })
        .catch((err) => console.error(err));
    }
  };

  const informAboutChangingIndustry = () => {
    const {
      userDataEC: { basketInfo, industry: industrycode },
    } = props;
    if (basketInfo) {
      const { basket = null, basketreference = null } = basketInfo;

      if ((basket && basketreference && industrycode) || (basket && industrycode && isAuthenticated())) {
        props.actionsJourney.patchBasketDefinition({
          basket,
          basketreference,
          industrycode,
        });
      }
    }
  };

  const step = params.step as unknown as number;

  return (
    <>
      <BreadCrumbs
        parentPage={
          <a
            className='color-text pointer'
            href={intl.formatMessage({
              id: 'ec.navigation.parentPlanningAndBookingLink',
              defaultMessage: 'https://www.apgsga.ch/de/aussenwerbung/plakate-online-buchen/',
            })}
          >
            {intl.formatMessage({
              id: 'ec.navigation.planenbuchen',
              defaultMessage: 'Planen und Buchen',
            })}
          </a>
        }
        pageName={intl.formatMessage({
          id: 'ec.navigation.journey2PageName',
          defaultMessage: 'Gezielte Werbeflächen auswählen',
        })}
      />

      <div
        className={classNames('container-fluid overflow-hidden journey2 p-0', {
          journey3: containers.isJourney3,
          copyCampaign: step > 100,
        })}
        id='journey2'
      >
        <div className={classNames('pageContainer', { 'pageContainer-wide': [4].includes(Number(step)) })}>
          <Basket
            products={props.userDataEC.basketProducts.productsByUrnDate}
            total={props.userDataEC.basketProducts.stats?.mediasum}
          />

          <ModalContainer
            actionsBasket={_actionsBasket}
            location={location}
            actionsJourney2={_actionsJourney2}
            userDataEC={props.userDataEC}
            actionsProduct={props.actionsProduct}
            errors={props.errors}
            intl={props.intl}
            loading={props.loading}
            product={props.product}
            productsJ2={props.productsJ2}
            productsJ2ByUrn={props.productsJ2ByUrn}
          />

          {(step <= 11 || step > 100) && (
            <ProgressBar
              keycloakReady={keycloakReady}
              keycloakReducer={keycloakReducer}
              step={step}
              mapView={mapView}
              setMapView={setMapView}
              subtitle={params.campaignId}
              journeyType={containers.isJourney3 ? 'journey3' : 'journey2'}
              stateStep6Loading={step6Loading}
            />
          )}

          <RouterContext.Provider value={history}>
            <div
              className={classNames('contentContainer', {
                perc10: step == 1,
                perc20: step == 2,
                perc30: step == 3,
                perc40: step == 4,
                perc50: step == 5,
                perc60: step == 6,
                perc70: step == 7,
                perc80: step == 8,
                perc90: step == 9,
                perc100: step == 10,
                perc110: step == 11,
              })}
            >
              <SwitchTransition mode='out-in'>
                <CSSTransition key={step} timeout={{ enter: 1000, exit: 500 }} classNames='fade'>
                  <div
                    className={classNames('main', {
                      full: step == 4,
                      'bg-transparent p-0': step == 5,
                    })}
                  >
                    {keycloakReady ? (
                      renderSwitch(
                        step,
                        {
                          1: () => <Step1 readyToRender={readyToRender} />,
                          2: () => <Step2 />,
                          3: () => <Step3 />,
                          4: () => (
                            <ResultsStep
                              mapView={mapView}
                              setMapView={setMapView}
                              setStepLoading={(value) => setStep6Loading(value)}
                            />
                          ),
                          5: () => <LoginRequired component={<Summary journey2 />} beforeLogin={handleLoginRedirect} />,
                          6: () => (
                            <LoginRequired
                              component={
                                <OrderConfirmation
                                  keycloakReady={keycloakReady}
                                  journey2
                                  loading={loading['general']}
                                />
                              }
                            />
                          ),
                        },
                        () => {
                          if (step > 100) {
                            return <Step1J3 />;
                          } else {
                            return <Page404 />;
                          }
                        },
                      )
                    ) : (
                      <SpinnerV2 relative fullScreen />
                    )}
                  </div>
                </CSSTransition>
              </SwitchTransition>

              {/*<SwitchTransition>*/}
              {/*    <CSSTransition key={step + 'aside'} timeout={{ enter: 1000, exit: 500 }} classNames="fade">*/}
              {/*        <div>*/}
              {/*            {[1, 2, 3, 4, 5].includes(Number(step)) && <Aside step={step} />}*/}
              {/*            {[7].includes(Number(step)) && <Aside total={1} step={step} />}*/}
              {/*            {[8].includes(Number(step)) && <Aside total={2} step={step} />}*/}
              {/*            {step > 100 && <Aside step={step} />}*/}
              {/*        </div>*/}
              {/*    </CSSTransition>*/}
              {/*</SwitchTransition>*/}
            </div>
          </RouterContext.Provider>
        </div>
      </div>

      <ServiceDesk intl={intl} />
    </>
  );
};

function mapStateToProps(state: RootState, ownProps: any) {
  return {
    ...ownProps,
    ...state.app,
    ...state.router,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    actions: bindActionCreators(actions as any, dispatch),
    dispatch: dispatch,
    actionsJourney: bindActionCreators(_actionsJourney as any, dispatch),
    actionsJourney2: bindActionCreators(_actionsJourney2 as any, dispatch),
    actionsJourney3: bindActionCreators(_actionsJourney3 as any, dispatch),
    actionsBasket: bindActionCreators(_actionsBasket as any, dispatch),
    actionsProduct: bindActionCreators(_actionsProduct as any, dispatch),
    actionsFilters: bindActionCreators(_actionsFilters as any, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Journey2);
